import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Mail, Clock, AlertCircle, CheckCircle, UserPlus } from 'lucide-react';
import { Spinner } from '../design-system';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { getApiBaseUrl } from '../utils/api';
import toast from '../design-system/toast';

interface InvitationDetails {
  id: string;
  organization_id: string;
  email: string;
  role: string;
  expires_at: string;
  status: string;
  organization_name: string;
  inviter_name: string;
}

const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const api = useAuthenticatedApi();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Public endpoint - doesn't require auth
        const response = await fetch(`${getApiBaseUrl()}/invitations/${token}`);
        
        if (!response.ok) {
          const data = await response.json();
          if (response.status === 404) {
            setError('This invitation is no longer valid or has already been used.');
          } else if (response.status === 410) {
            setError('This invitation has expired.');
          } else {
            setError(data.detail || 'Failed to load invitation');
          }
          return;
        }

        const data = await response.json();
        setInvitation(data);
      } catch (err: any) {
        console.error('Failed to fetch invitation:', err);
        setError('Failed to load invitation. Please check the link and try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Handle accept invitation
  const handleAccept = async () => {
    if (!token || !isAuthenticated) return;

    try {
      setIsAccepting(true);
      setError(null);

      await api.post(`/invitations/${token}/accept`);
      setAccepted(true);
      toast.success(`You've joined ${invitation?.organization_name}!`);

      // Redirect to applications after a brief delay
      setTimeout(() => {
        navigate('/repositories', { replace: true });
      }, 2000);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      const errorMessage = err.data?.detail || err.message || 'Failed to accept invitation';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAccepting(false);
    }
  };

  // Handle sign in redirect
  const handleSignIn = () => {
    // Store the current URL (incl. query) to redirect back after sign in
    sessionStorage.setItem('redirect_after_auth', window.location.pathname + window.location.search);
    navigate('/');
  };

  // Handle sign up redirect
  const handleSignUp = () => {
    sessionStorage.setItem('redirect_after_auth', window.location.pathname + window.location.search);
    navigate('/sign-up');
  };

  // Check if user's email matches the invitation
  const emailMismatch = isAuthenticated && invitation && user?.email?.toLowerCase() !== invitation.email.toLowerCase();

  // Loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex items-center justify-center">
        <div className="text-center">
          <Spinner size={32} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-ui-text-primary mb-2">
            Invalid Invitation
          </h1>
          <p className="text-ui-text-secondary mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black font-medium rounded-lg transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (accepted) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-ui-text-primary mb-2">
            Welcome to {invitation?.organization_name}!
          </h1>
          <p className="text-ui-text-secondary mb-4">
            You've successfully joined the organization as a {invitation?.role}.
          </p>
          <p className="text-sm text-ui-text-tertiary">
            Redirecting you to your projects...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-primary/10 dark:bg-brand-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-brand-primary dark:text-brand-primaryDarkMode" />
          </div>
          <h1 className="text-2xl font-bold text-ui-text-primary mb-2">
            You're Invited!
          </h1>
          <p className="text-ui-text-secondary">
            You've been invited to join an organization
          </p>
        </div>

        {/* Invitation Card */}
        <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary p-6 mb-6">
          {/* Organization Name */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-brand-primary/10 dark:bg-brand-primary/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-brand-primary dark:text-brand-primaryDarkMode" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ui-text-primary">
                {invitation?.organization_name}
              </h2>
              <p className="text-sm text-ui-text-tertiary capitalize">
                Join as {invitation?.role}
              </p>
            </div>
          </div>

          {/* Invited By */}
          {invitation?.inviter_name && (
            <div className="flex items-center gap-2 text-sm text-ui-text-secondary mb-3">
              <Mail className="w-4 h-4" />
              <span>Invited by {invitation.inviter_name}</span>
            </div>
          )}

          {/* Expiry */}
          <div className="flex items-center gap-2 text-sm text-ui-text-secondary">
            <Clock className="w-4 h-4" />
            <span>
              Expires {new Date(invitation?.expires_at || '').toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Email Mismatch Warning */}
        {emailMismatch && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> This invitation was sent to{' '}
              <span className="font-medium">{invitation?.email}</span>, but you're signed in as{' '}
              <span className="font-medium">{user?.email}</span>.
            </p>
          </div>
        )}

        {/* Actions */}
        {isAuthenticated ? (
          <div className="space-y-3">
            <button
              onClick={handleAccept}
              disabled={isAccepting}
              className="w-full py-3 px-4 bg-brand-primary hover:bg-brand-primaryDark disabled:opacity-50 
                         text-black font-medium rounded-lg transition-colors
                         flex items-center justify-center gap-2
                         disabled:cursor-not-allowed"
            >
              {isAccepting ? (
                <>
                  <Spinner size={20} tone="current" />
                  Joining...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Accept Invitation
                </>
              )}
            </button>

            <button
              onClick={() => navigate('/repositories')}
              className="w-full py-3 px-4 text-ui-text-secondary
                         hover:bg-ui-background-tertiary
                         font-medium rounded-lg transition-colors"
            >
              Maybe Later
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-center text-sm text-ui-text-secondary mb-4">
              Sign in or create an account to accept this invitation
            </p>

            <button
              onClick={handleSignIn}
              className="w-full py-3 px-4 bg-brand-primary hover:bg-brand-primaryDark
                         text-black font-medium rounded-lg transition-colors"
            >
              Sign In
            </button>

            <button
              onClick={handleSignUp}
              className="w-full py-3 px-4 border border-ui-border-primary
                         text-ui-text-secondary
                         hover:bg-ui-background-tertiary
                         font-medium rounded-lg transition-colors"
            >
              Create Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
